/**
 * Hook to resolve the authenticated user's email to their AP_Master record.
 *
 * Uses useAuth() for the current user's email, then queries AP_Master
 * searching ALL statuses (Active, Inactive, Onboarding) so archived APs
 * can still resolve their profile for self-remediation.
 *
 * When a login owns SEVERAL rows (archived-then-re-onboarded, or accumulated test
 * rows — AP_Master has no uniqueness constraint on Email), the winner is chosen by
 * `resolveAPIdentity`: Active > Onboarding > Inactive, newest row breaking ties.
 * Never by list order. That is the SAME published rule `api/shared/ap.js` now
 * applies server-side, which is the whole point — when the two sides named
 * different rows the backend ANDed the two filters together and every list came
 * back empty with an HTTP 200 and no log line at all (ISS-032).
 *
 * ---------------------------------------------------------------------------
 * The outcome reaches this hook by TWO roads, and they must agree:
 *
 *   1. From the ROWS. `readList('apMaster')` deliberately returns every row
 *      matching the caller's identity forms — including an Inactive one — so an
 *      archived AP can still render the compliance experience and so the
 *      candidate rows can be named on screen. `resolveAPIdentity` reads the
 *      verdict off those rows.
 *   2. From a REFUSAL. A login that owns no row, or owns one with no APCode,
 *      never receives rows at all: the backend answers the read itself with a
 *      403 carrying a named `code`. `outcomeFromAPErrorCode` turns that back
 *      into the same outcome.
 *
 * Both roads set `outcome`, so the UI has ONE switch instead of two. A named
 * refusal is an ANSWER, not a fault: it sets `outcome` and leaves `error` null,
 * because "we couldn't load your profile, try again" is the wrong sentence for
 * a provider whose record simply has no code on it.
 *
 * Caches the result in a ref to avoid re-fetching.
 * Returns null apId when no AP_Master match (e.g., admin user testing) — an
 * internal Admin browsing /ap/* legitimately owns no row and must see no fault.
 */

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../auth/useAuth';
import { demangleGuestUpn } from '../auth/authConfig';
import { useGraphClient } from './useGraphClient';
import { listItems } from '../services/graphClient';
import { GraphApiError } from '../services/graphError';
import {
  outcomeFromAPErrorCode,
  resolveAPIdentity,
} from '../services/currentAPResolution';
import type {
  APIdentityCandidate,
  APIdentityOutcome,
} from '../services/currentAPResolution';
import type { APMasterItem, APStatus } from '../services/apMasterService';

export interface UseCurrentAPResult {
  /** Full matched AP_Master record — carries the v1.2 compliance attributes. */
  ap: APMasterItem | null;
  apId: string | null;
  apName: string | null;
  tier: string | null;
  status: APStatus | null;
  /**
   * The effective organisation's friendly name (the parent AP row's Title) for
   * a worker under a parent org — else null (sole trader / org head). Resolved
   * from the SAME AP_Master read as `ap` (no extra fetch), mirroring the
   * CM-side `orgNameFor`. Falls back to the raw ParentAPCode string if the
   * parent row isn't present, so worker-facing copy never regresses to blank.
   */
  orgName: string | null;
  isLoading: boolean;
  error: string | null;
  /** Diagnostic: number of AP rows the lookup read (null until resolved). */
  recordsRead: number | null;
  /** Diagnostic: whether the Graph client (site/list resolution) is ready. */
  graphReady: boolean;
  /** Diagnostic: Graph client resolution error, if any. */
  graphError: string | null;
  /**
   * WHY the app looks the way it does — `active`, `not-active`, `no-match` or
   * `no-apcode`. Null only before resolution completes, or when the lookup
   * failed for a reason that is not an identity verdict (see `error`).
   */
  outcome: APIdentityOutcome | null;
  /** True only for `outcome === 'active'`. The one question every gate asks. */
  accessGranted: boolean;
  /**
   * More than one owned row shares the winning status band. RESOLVED, never
   * blocking — the app runs normally. Reported so somebody is told, because an
   * ambiguity is Harmony's problem to fix, not the provider's to read about.
   */
  ambiguous: boolean;
  /**
   * PII-free descriptors of every owned row, winner first — `{id, apCode,
   * status}`, never an email or a name. The same descriptors 35-02 writes to
   * the `[ap-identity]` server log, so the screen and the log say the same
   * thing about the same sign-in.
   */
  candidates: APIdentityCandidate[];
}

export function useCurrentAP(): UseCurrentAPResult {
  const { user } = useAuth();
  const { client, siteId, listIds, isReady, error: graphError } = useGraphClient();

  const [ap, setAp] = useState<APMasterItem | null>(null);
  const [apId, setApId] = useState<string | null>(null);
  const [apName, setApName] = useState<string | null>(null);
  const [tier, setTier] = useState<string | null>(null);
  const [status, setStatus] = useState<APStatus | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Diagnostic: how many AP rows the read actually returned (null until resolved).
  const [recordsRead, setRecordsRead] = useState<number | null>(null);
  // The named verdict — from the rows, or from the refusal that replaced them.
  const [outcome, setOutcome] = useState<APIdentityOutcome | null>(null);
  const [accessGranted, setAccessGranted] = useState(false);
  const [ambiguous, setAmbiguous] = useState(false);
  const [candidates, setCandidates] = useState<APIdentityCandidate[]>([]);

  // Cache: once resolved, don't re-fetch
  const resolvedRef = useRef(false);

  // Content-stable key over the login identities — a primitive so the effect
  // dependency doesn't churn each render (getLoginIdentities returns a fresh
  // array every call, and the build has no React Compiler to memoize it).
  const identityKey = (user?.identities ?? []).join('\n');

  useEffect(() => {
    if (!isReady || !client || !siteId || !listIds || !identityKey) return;
    if (resolvedRef.current) return;

    let cancelled = false;
    setIsLoading(true);

    // Fetch ALL APs (not just Active) so archived APs can resolve their profile
    listItems<APMasterItem>(client, siteId, listIds.apMaster)
      .then((result) => {
        if (cancelled) return;
        resolvedRef.current = true;
        setRecordsRead(result.items.length);

        // Match the AP against every identity form this login can present
        // (plain email, #ext# UPN, and the de-mangled email) — see getLoginIdentities.
        // The matcher is EXACT lowercase Email equality plus the #ext# demangle,
        // and it is pinned by api/shared/apIdentity.cases.json. No semicolon
        // splitting: OH-001's semicolon-joined Email is accidentally the only
        // thing keeping Nicole Azari resolving to NA-001 (ISS-027/ISS-032), and
        // "helpfully" splitting it silently moves her to another provider's row.
        const loginIds = new Set(identityKey.split('\n'));
        // Status-first precedence (Active > Onboarding > Inactive), NOT list order.
        // One person legitimately owns several rows — archived then re-onboarded —
        // and first-match-wins resolved the stale Inactive one, stranding a live AP
        // on the archived page. See selectCurrentAPRecord.
        const identity = resolveAPIdentity(result.items, (ap) => {
          if (!ap.Email) return false;
          if (loginIds.has(ap.Email.toLowerCase())) return true;
          const plain = demangleGuestUpn(ap.Email);
          return plain ? loginIds.has(plain) : false;
        });

        setOutcome(identity.outcome);
        setAccessGranted(identity.accessGranted);
        setAmbiguous(identity.ambiguous);
        setCandidates(identity.candidates);

        const match = identity.record;
        if (match) {
          setAp(match);
          // The TRIMMED APCode from the resolution, which is byte-for-byte what
          // `ctx.apCode` is server-side — and null when the row carries none, so
          // a blank code can never become the string 'undefined' in a filter.
          setApId(identity.apCode);
          setApName(match.Title);
          setTier(match.Tier);
          setStatus(match.Status);
          // Resolve the effective org's friendly name from the SAME read: when
          // this AP is a worker under a parent org, look up the parent row's
          // Title (no extra network call). Fall back to the raw ParentAPCode
          // string if the parent row isn't in the set, so worker copy never
          // regresses to blank. Null for a sole trader / org head.
          const parentCode = match.ParentAPCode?.trim();
          if (parentCode && parentCode !== match.APCode) {
            const parent = result.items.find((r) => r.APCode === parentCode);
            setOrgName(parent?.Title?.trim() || parentCode);
          } else {
            setOrgName(null);
          }
        }
        // If no match, apId stays null — user may be admin testing
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;

        // A NAMED identity refusal is an answer, not a fault. The backend
        // refuses the apMaster read outright for a login that owns no row
        // (AP_IDENTITY_NO_MATCH) or owns one with no provider code
        // (AP_IDENTITY_NO_APCODE), so those two outcomes arrive here as a 403
        // rather than as rows. Record the outcome and leave `error` null: the
        // UI then says which of the four things happened instead of "we
        // couldn't load your profile, try again" — the sentence that made a
        // blank app indistinguishable from a broken one on 2026-09-02.
        const refused =
          err instanceof GraphApiError ? outcomeFromAPErrorCode(err.code) : null;
        if (refused) {
          setOutcome(refused);
          setAccessGranted(false);
          setError(null);
          return;
        }

        setError(
          err instanceof Error ? err.message : 'Failed to resolve AP profile',
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isReady, client, siteId, listIds, identityKey]);

  return {
    ap,
    apId,
    apName,
    tier,
    status,
    orgName,
    isLoading,
    error,
    recordsRead,
    graphReady: isReady,
    graphError,
    outcome,
    accessGranted,
    ambiguous,
    candidates,
  };
}
